import { Colors, FontFamily } from '@styles/index';

export const toolbarSx = {
  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: { xs: '56px', sm: '60px', md: '68px', lg: '72px' },
  height: { xs: '56px', sm: '60px', md: '68px', lg: '72px' },
  px: { xs: 1.5, sm: 2, md: 3, lg: 4, xl: 5 },
  width: '100%',
  maxWidth: '100%',
  py: 0,
  backgroundColor: 'transparent',
  background: 'transparent',
  gap: { xs: 1, sm: 1.5, md: 2 },
} as const;

export const leftSectionBoxSx = {
  // Equal flex share with the right section keeps the center navigation in the middle of the header
  flex: '1 1 0',
  display: 'flex',
  alignItems: 'center',
  gap: { xs: 2, sm: 2.5, md: 3 },
} as const;

export const whoLogoSx = {
  height: { xs: '28px', sm: '32px', md: '36px', lg: '42px', xl: '46px' },
  width: 'auto',
  objectFit: 'contain',
  display: 'block',
  cursor: 'pointer',
  transition: 'opacity 0.2s ease, transform 0.2s ease',
} as const;

export const brandTitleContainerSx = {
  flex: '1 1 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: { xs: 'flex-start', md: 'center' },
  minWidth: 0,
} as const;

export const brandTitleTypographySx = {
  fontWeight: 800,
  fontSize: { xs: '17px', sm: '19px', md: '21px', lg: '24px' },
  letterSpacing: '-0.3px',
  fontFamily: FontFamily.primary,
  whiteSpace: 'nowrap',
  lineHeight: 1,
  color: Colors.navy,
} as const;

export const centerNavContainerSx = {
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 0,
  overflow: 'visible',
} as const;

export const navPillContainerSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: { md: 0.25, lg: 0.5, xl: 0.75 },
  backgroundColor: 'rgba(255, 255, 255, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: '50px',
  padding: { md: '4px 6px', lg: '5px 8px', xl: '6px 10px' },
  border: '1px solid rgba(0, 0, 0, 0.06)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  flexShrink: 0,
} as const;

/** Menu bar + Search button; stretch makes the button exactly as tall as the menu bar */
export const navAndSearchRowSx = {
  display: 'flex',
  alignItems: 'stretch',
  gap: { md: 1, lg: 1.5 },
} as const;

/** "Search" button next to the menu — same glass pill look as the menu bar */
export const headerSearchButtonSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  px: { md: 1.5, lg: 2 },
  cursor: 'pointer',
  backgroundColor: 'rgba(255, 255, 255, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: '50px',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  fontFamily: FontFamily.primary,
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    boxShadow: '0 3px 10px rgba(102, 126, 234, 0.2)',
    transform: 'translateY(-1px)',
  },
  '&:focus-visible': {
    outline: '2px solid #667eea',
    outlineOffset: '2px',
  },
} as const;

export const headerSearchLabelSx = {
  fontSize: { md: '12px', lg: '13px', xl: '14px' },
  fontWeight: 600,
  color: '#374151',
  whiteSpace: 'nowrap',
  lineHeight: 1,
} as const;

export const rightSectionBoxSx = {
  flex: { xs: '0 0 auto', md: '1 1 0' },
  justifyContent: 'flex-end',
  minWidth: { xs: '40px', sm: '50px', md: 'auto' },
} as const;

export const userProfileClickableSx = {
  flexGrow: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  cursor: 'pointer',
  px: 1,
  py: 0.5,
  borderRadius: 1,
  '&:hover': {
    bgcolor: 'rgba(0, 28, 83, 0.08)',
  },
} as const;

export const userNameTypographySx = {
  display: { xs: 'none', sm: 'block' },
  color: Colors.navy,
  fontWeight: 500,
  fontSize: { sm: '0.875rem', md: '0.9375rem' },
  maxWidth: { sm: '120px', md: '150px' },
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

export const profileIconButtonSx = {
  p: 0.5,
  color: Colors.navy,
  '&:hover': {
    bgcolor: 'transparent',
  },
} as const;

export const personOutlineIconSx = {
  fontSize: { xs: 28, sm: 30, md: 32 },
} as const;

export const userMenuSx = {
  mt: '45px',
} as const;

export const menuPaperSx = {
  maxHeight: 'calc(100vh - 100px)',
} as const;

export const userEmailMenuItemTypographySx = {
  textAlign: 'center',
  width: '100%',
} as const;

export const unauthenticatedAccountButtonSx = {
  color: Colors.navy,
  '&:hover': {
    bgcolor: 'rgba(0, 28, 83, 0.08)',
  },
} as const;

export const mobileHamburgerButtonSx = {
  ml: 0.5,
  color: '#0d1b52',
  '&:hover': {
    bgcolor: 'rgba(13, 27, 82, 0.08)',
  },
} as const;

export const mobileDrawerSx = {
  zIndex: 1400,
  '& .MuiDrawer-paper': {
    width: { xs: '75%', sm: '300px' },
    maxWidth: '300px',
    zIndex: 1400,
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.1)',
  },
} as const;

export const drawerContainerBoxSx = {
  width: '100%',
  height: '100%',
} as const;

export const drawerHeaderBoxSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  p: 2,
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
} as const;

export const drawerCloseButtonSx = {
  '&:hover': {
    bgcolor: 'rgba(0, 0, 0, 0.04)',
  },
} as const;

export const drawerNavListSx = {
  pt: 2,
} as const;

export const drawerLoadingBoxSx = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  py: 2,
} as const;

export const drawerNavLinkButtonSx = {
  py: 1.5,
  px: 3,
  '&:hover': {
    bgcolor: 'rgba(25, 118, 210, 0.08)',
  },
} as const;

export const externalLinkIndicatorSpanSx = {
  ml: 1,
  fontSize: '0.75rem',
  opacity: 0.6,
} as const;

export const drawerDividerSx = {
  mx: 2,
} as const;
